import { useState } from "react";
import { Link } from "react-router-dom";
import { Nfc, QrCode, UserPlus, Link2, BarChart3, Palette, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Navbar } from "@/components/layout/Navbar";
import { CardView, PhoneFrame } from "@/components/card/CardView";
import { EMPTY_CARD, downloadVCard } from "@/lib/card";

const COVER = "https://images.unsplash.com/photo-1516131206008-dd041a9764fd?crop=entropy&cs=srgb&fm=jpg&w=800&q=70";
const DEMO = {
  ...EMPTY_CARD, slug: "demo", company_name: "Studio Rossi & Partners", contact_name: "Giulia Rossi", role: "Fondatrice",
  piva: "IT01234567890", phone: "+39 02 1234567", email: "info@studiorossi.it", whatsapp: "+39 333 1234567",
  address: "Via Montenapoleone 8, Milano", website: "studiorossi.it", hours: "Lun–Ven 9:00–18:00",
  description: "Consulenza strategica e branding per PMI italiane.",
  socials: { ...EMPTY_CARD.socials, linkedin: "linkedin.com", instagram: "instagram.com" },
  links: [{ id: "1", label: "Scarica il catalogo", url: "taptolink.it" }],
  logo_url: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
};

const FEATURES = [
  { icon: Nfc, title: "Tecnologia NFC", text: "Un tocco con lo smartphone sulla tua carta taptolink e il cliente apre la tua scheda.", span: "md:col-span-2" },
  { icon: UserPlus, title: "Salva in Rubrica", text: "File vCard generato al volo per iPhone e Android." },
  { icon: QrCode, title: "QR Code dinamico", text: "Scarica il QR in alta risoluzione per stampe e vetrine." },
  { icon: Link2, title: "Link & Social", text: "Sito, catalogo, LinkedIn, Instagram: tutto in un'unica pagina.", span: "md:col-span-2" },
  { icon: BarChart3, title: "Statistiche reali", text: "Visite, click sui link e salvataggi in rubrica, giorno per giorno.", span: "md:col-span-2" },
  { icon: Palette, title: "Temi premium", text: "Quattro temi e colore accento del tuo brand." },
];

const STEPS = [
  ["01", "Registrati", "Crea il tuo account in meno di un minuto."],
  ["02", "Componi la scheda", "Logo, P.IVA, contatti, link e social con anteprima live."],
  ["03", "Condividi", "Via link, QR code o carta NFC su carta.taptolink.it."],
];

const Hero = () => (
  <section className="relative overflow-hidden grid-bg">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 py-20 lg:grid-cols-12 lg:py-28">
      <div className="lg:col-span-7">
        <p className="rise text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Identità Impresa · taptolink.it</p>
        <h1 className="rise rise-1 mt-6 text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
          Il tuo biglietto da visita<br />digitale <span className="text-blue-600">& NFC</span>.
        </h1>
        <p className="rise rise-2 mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
          La tua identità aziendale in un solo tocco. Una scheda professionale con contatti, P.IVA, link e social, sempre aggiornata.
        </p>
        <div className="rise rise-3 mt-10 flex flex-wrap gap-3">
          <Button asChild size="lg" className="h-12 rounded-full bg-blue-600 px-7 hover:bg-blue-700" data-testid="hero-cta-button">
            <Link to="/sign-up">Crea la tua scheda gratis <ArrowRight size={16} className="ml-1" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 rounded-full px-7" data-testid="hero-demo-button">
            <a href="#prova">Prova la demo</a>
          </Button>
        </div>
        <div className="rise rise-4 mt-14 flex gap-10 text-sm">
          {[["1 tocco", "per condividere"], ["0 app", "da installare"], ["100%", "aggiornabile"]].map(([a, b]) => (
            <div key={a}><p className="font-display text-2xl font-bold">{a}</p><p className="text-slate-500">{b}</p></div>
          ))}
        </div>
      </div>
      <div className="rise rise-2 lg:col-span-5">
        <PhoneFrame><CardView card={DEMO} coverFallback={COVER} onVCard={() => downloadVCard(DEMO)} /></PhoneFrame>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="funzionalita" className="mx-auto max-w-7xl px-6 py-24">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Funzionalità</p>
    <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Tutto quello che serve alla tua impresa per farsi ricordare.</h2>
    <div className="mt-14 grid gap-6 md:grid-cols-4">
      {FEATURES.map(({ icon: Icon, title, text, span }) => (
        <div key={title} className={`group rounded-2xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${span || ""}`}>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white transition-colors group-hover:bg-blue-600"><Icon size={20} /></span>
          <h3 className="mt-6 text-lg font-semibold">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
        </div>
      ))}
    </div>
  </section>
);

const LiveDemo = () => {
  const [card, setCard] = useState({ ...DEMO, theme: "emerald_gold", accent: "#F59E0B" });
  const set = (k) => (e) => setCard({ ...card, [k]: e.target.value });
  return (
    <section id="prova" className="relative overflow-hidden bg-slate-900 py-24 text-white noise">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-400">Prova live</p>
          <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Scrivi e guarda la tua scheda prendere forma.</h2>
          <div className="mt-10 max-w-md space-y-4">
            <Input value={card.company_name} onChange={set("company_name")} placeholder="Nome impresa" data-testid="demo-input-company" className="h-12 border-slate-700 bg-slate-800 text-white" />
            <Input value={card.contact_name} onChange={set("contact_name")} placeholder="Referente" data-testid="demo-input-name" className="h-12 border-slate-700 bg-slate-800 text-white" />
            <Input value={card.role} onChange={set("role")} placeholder="Ruolo" data-testid="demo-input-role" className="h-12 border-slate-700 bg-slate-800 text-white" />
          </div>
        </div>
        <PhoneFrame><CardView card={card} coverFallback={COVER} onVCard={() => downloadVCard(card)} /></PhoneFrame>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section id="come-funziona" className="mx-auto max-w-7xl px-6 py-24">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Come funziona</p>
    <div className="mt-12 grid gap-12 md:grid-cols-3">
      {STEPS.map(([n, title, text]) => (
        <div key={n} className="border-t-2 border-slate-900 pt-6">
          <p className="font-mono-ui text-sm text-blue-600">{n}</p>
          <h3 className="mt-3 text-xl font-semibold">{title}</h3>
          <p className="mt-2 text-slate-600">{text}</p>
        </div>
      ))}
    </div>
    <div className="mt-24 flex flex-col items-start justify-between gap-8 rounded-3xl bg-blue-600 p-10 text-white md:flex-row md:items-center md:p-14">
      <h2 className="max-w-xl text-2xl font-bold tracking-tight sm:text-3xl">Pronto a lasciare il segno al primo tocco?</h2>
      <Button asChild size="lg" className="h-12 rounded-full bg-white px-7 text-slate-900 hover:bg-slate-100" data-testid="footer-cta-button">
        <Link to="/sign-up">Inizia ora</Link>
      </Button>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <Hero />
      <Features />
      <LiveDemo />
      <HowItWorks />
      <footer className="border-t border-slate-200 py-10 text-center text-sm text-slate-500">© {new Date().getFullYear()} Identità Impresa · taptolink.it</footer>
    </div>
  );
}
